import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Analytics, OpenInNew } from '@mui/icons-material'

import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import SubmissionsViewer from '../assignments/SubmissionsViewer'
import CustomTable from '../../common/custom-table/CustomTable'
import noAssignmentImg from '../../../assets/noassignment.svg'
import AssignmentViewer from '../../common/AssignmentViewer'
import { formatDate, Icons } from '../../../utils/utilities'
import api from '../../../service/api'


const AssignmentDetails = ( { userData } ) => {

    const [submissionViewerState, setSubmissionViewerState] = useState( false )
    const [selectedAssignment, setSelectedAssignment] = useState( null )
    const [assignmentDetails, setAssignmentDetails] = useState( [] )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { layout } = useTheme()

    const { empcode } = useParams()
    const [searchParams] = useSearchParams( { legacy: true } )
    const legacy = searchParams.get( 'legacy' ) === 'true'


    const columns = useMemo( () => [
        { id: 1, width: "100px", field: "index", index: true, name: "Sl. no.", align: "center" },
        { id: 2, width: "200px", field: "assignment_name", name: "Assignment Title" },
        { id: 3, width: "130px", field: "created_at", renderCell: ( item ) => formatDate( item.assignment_created_at ), name: "Created on" },
        { id: 4, field: "subject", width: "200px", renderCell: item => item?.assignment_subject?.subject_name || "Not Available", name: "Subject" },
        { id: 5, field: "assignment_submission", name: "Submissions", align: "center" },
        {
            id: 6, field: "actions", renderCell: item => ( <Box display="flex" justifyContent="right">
                <Tooltip title="Open details">
                    <IconButton onClick={() => setSelectedAssignment( item )} color="primary" size="small"><OpenInNew fontSize='small' /></IconButton>
                </Tooltip>
                {+item.assignment_submission > 0 && <Tooltip title="View submissions">
                    <IconButton onClick={() => { setSelectedAssignment( item ); setSubmissionViewerState( true ) }} color="secondary" size="small"><Analytics fontSize='small' /></IconButton>
                </Tooltip>}
            </Box> ), name: "Actions", align: "right"
        },
    ] )

    const loadAssingments = useCallback( async () => {
        setLoading( true )
        try {
            const { data: { data } } = await api.audit.getAssignmentsByFacultyId( empcode, { legacy } )
            setAssignmentDetails( data )
            setError( false )
        } catch ( err ) {
            setError( true )
        } finally {
            setLoading( false )
        }
    }, [empcode, legacy] )

    useEffect( () => {
        loadAssingments()
    }, [loadAssingments] )

    return (
        <Box borderRadius={layout.borderRadius} maxHeight="350px" display="flex" flexDirection="column" overflow="hidden">

            <AssignmentViewer onClose={() => setSelectedAssignment( null )} loaded={true} selectedValue={selectedAssignment} open={!submissionViewerState && Boolean( selectedAssignment )} />
            {selectedAssignment && submissionViewerState && <SubmissionsViewer selectedValue={selectedAssignment} handleClose={() => { setSubmissionViewerState( false ); setSelectedAssignment( null ) }} assignmentId={selectedAssignment?.assignment_id} open={submissionViewerState} />}

            {/* Data */}
            {!loading && !error && assignmentDetails.length > 0 && <CustomTable columns={columns} data={assignmentDetails} />}

            {/* Loading */}
            {loading && !error && <Box padding="50px 20px" display="flex" justifyContent="center">
                <DataLoadingSpinner waitingMessage="Please wait! Loading assignments..." />
            </Box>}

            {/* Error */}
            {error && <TryAgainMessageBlock getData={loadAssingments} loading={loading} err={error} />}

            {/* No Data */}
            {!loading && !error && assignmentDetails.length === 0 && <Box padding="20px" gap="20px" display="flex" alignItems="center" flexDirection="column" justifyContent="center">
                <img src={noAssignmentImg} height="100px" alt="Something went wrong!" />
                <Typography variant="subtitle2"><em>{userData.employee_name}</em> has not given any assignments!</Typography>
                <Button disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay} onClick={loadAssingments} variant="outlined" sx={{ textTransform: "capitalize" }} color="secondary">Fetch again</Button>
            </Box>}
        </Box>
    )
}

export default AssignmentDetails
